import React, { useState, useRef, useEffect } from 'react';

// ============================================
// Floating Action Button — bottom-right corner, logged-in users only.
// Expands into a "Quick Actions" menu; pass options as an array so new
// actions are one-line additions in App.jsx.
// option: { icon, label, sublabel?, onClick, accent?, badge? }
//   accent — hex used to tint the option's icon tile (defaults to purple)
//   badge  — short string ("OPEN", "3") rendered as a pill on the option row;
//            any badged option also puts an attention dot + gold pulse on the orb
// nudge (optional): { id, icon, text, onClick } — animated pill that slides out
//   next to the orb for time-sensitive actions (e.g. lossback window open).
//   Dismissal is stored per-id in localStorage, so a new id re-shows it.
// ============================================

const PURPLE = '#a855f7';
const GOLD = '#fbbf24';

export default function Fab({ options = [], nudge = null }) {
  const [open, setOpen] = useState(false);
  const [nudgeDismissed, setNudgeDismissed] = useState(() =>
    nudge ? !!localStorage.getItem(`fabNudgeDismissed_${nudge.id}`) : true
  );
  const rootRef = useRef(null);

  // Re-evaluate dismissal when the nudge id changes (new lossback window etc.)
  useEffect(() => {
    setNudgeDismissed(nudge ? !!localStorage.getItem(`fabNudgeDismissed_${nudge.id}`) : true);
  }, [nudge?.id]);

  // Close when clicking anywhere outside, or on Escape
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (options.length === 0) return null;

  const badgedCount = options.filter(o => o.badge).length;
  const attention = badgedCount > 0;
  const showNudge = nudge && !nudgeDismissed && !open;

  const dismissNudge = () => {
    localStorage.setItem(`fabNudgeDismissed_${nudge.id}`, '1');
    setNudgeDismissed(true);
  };

  return (
    <div ref={rootRef} className="fab-root">
      <style>{`
        .fab-root {
          position: fixed;
          bottom: 24px;
          right: 24px;
          z-index: 1200;
          display: flex;
          flex-direction: column;
          align-items: flex-end;
          gap: 0.8rem;
          font-family: 'Outfit', sans-serif;
        }

        /* ===== MENU ===== */
        .fab-menu {
          width: 276px;
          padding: 0.55rem;
          border-radius: 18px;
          background: linear-gradient(180deg, rgba(20, 18, 28, 0.98), rgba(12, 12, 18, 0.98));
          border: 1px solid rgba(168, 85, 247, 0.32);
          box-shadow: 0 24px 60px rgba(0, 0, 0, 0.62), 0 0 40px rgba(168, 85, 247, 0.18);
          transform-origin: bottom right;
          animation: fab-menu-in 0.24s cubic-bezier(0.22, 1, 0.36, 1);
          backdrop-filter: blur(14px);
        }
        @keyframes fab-menu-in {
          from { opacity: 0; transform: translateY(12px) scale(0.94); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }

        /* Header */
        .fab-menu-header {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.5rem 0.65rem 0.7rem;
        }
        .fab-menu-title {
          font-size: 0.7rem;
          font-weight: 800;
          letter-spacing: 1.6px;
          text-transform: uppercase;
          color: #c4b5fd;
        }
        .fab-menu-header-line {
          flex: 1;
          height: 1px;
          background: linear-gradient(90deg, rgba(168, 85, 247, 0.45), transparent);
        }

        /* ===== OPTION ===== */
        .fab-option {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          width: 100%;
          padding: 0.6rem 0.65rem;
          border-radius: 12px;
          border: none;
          background: none;
          color: #e4e4e7;
          font-family: 'Outfit', sans-serif;
          text-align: left;
          cursor: pointer;
          transition: background 0.18s ease, transform 0.18s ease;
        }
        .fab-option + .fab-option { margin-top: 0.15rem; }
        .fab-option:hover { background: rgba(168, 85, 247, 0.12); }
        .fab-option:active { transform: scale(0.98); }
        /* Badged options get a faint standing highlight so they read as "live" */
        .fab-option.badged {
          background: linear-gradient(90deg, rgba(251, 191, 36, 0.07), transparent 70%);
        }
        .fab-option.badged:hover { background: rgba(251, 191, 36, 0.12); }

        .fab-option-icon {
          width: 38px; height: 38px;
          display: flex; align-items: center; justify-content: center;
          font-size: 1.15rem;
          border-radius: 11px;
          flex-shrink: 0;
          background: var(--fab-accent-bg);
          border: 1px solid var(--fab-accent-border);
          transition: transform 0.18s ease;
        }
        .fab-option:hover .fab-option-icon { transform: scale(1.08) rotate(-4deg); }

        .fab-option-body { flex: 1; min-width: 0; }
        .fab-option-label {
          display: flex;
          align-items: center;
          gap: 0.45rem;
          font-size: 0.9rem;
          font-weight: 700;
          color: #fff;
          line-height: 1.2;
        }
        .fab-option-badge {
          font-size: 0.56rem;
          font-weight: 900;
          letter-spacing: 1px;
          padding: 0.12rem 0.45rem;
          border-radius: 999px;
          background: linear-gradient(135deg, #fbbf24, #f59e0b);
          color: #1c1205;
          animation: fab-badge-blink 1.6s ease-in-out infinite;
          flex-shrink: 0;
        }
        @keyframes fab-badge-blink {
          0%, 100% { opacity: 1; }
          50%      { opacity: 0.65; }
        }
        .fab-option-sublabel {
          display: block;
          font-size: 0.72rem;
          font-weight: 500;
          color: #8b8b96;
          margin-top: 0.12rem;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .fab-option-arrow {
          flex-shrink: 0;
          color: #6b6b78;
          font-size: 0.95rem;
          opacity: 0;
          transform: translateX(-4px);
          transition: opacity 0.18s ease, transform 0.18s ease, color 0.18s ease;
        }
        .fab-option:hover .fab-option-arrow {
          opacity: 1;
          transform: translateX(0);
          color: #c4b5fd;
        }

        /* ===== NUDGE PILL ===== */
        .fab-nudge-row {
          display: flex;
          align-items: center;
          gap: 0.8rem;
        }
        .fab-nudge {
          display: flex;
          align-items: center;
          gap: 0.55rem;
          padding: 0.55rem 0.6rem 0.55rem 0.9rem;
          border-radius: 999px;
          border: 1px solid rgba(251, 191, 36, 0.45);
          background: linear-gradient(135deg, rgba(35, 28, 10, 0.97), rgba(20, 16, 8, 0.97));
          box-shadow: 0 10px 32px rgba(0, 0, 0, 0.55), 0 0 24px rgba(251, 191, 36, 0.22);
          cursor: pointer;
          animation: fab-nudge-in 0.5s cubic-bezier(0.22, 1, 0.36, 1), fab-nudge-bob 3.2s ease-in-out 0.6s infinite;
          backdrop-filter: blur(10px);
          max-width: min(320px, calc(100vw - 130px));
        }
        @keyframes fab-nudge-in {
          from { opacity: 0; transform: translateX(24px); }
          to   { opacity: 1; transform: translateX(0); }
        }
        @keyframes fab-nudge-bob {
          0%, 100% { transform: translateY(0); }
          50%      { transform: translateY(-3px); }
        }
        .fab-nudge-icon { font-size: 1.05rem; flex-shrink: 0; }
        .fab-nudge-text {
          font-size: 0.8rem;
          font-weight: 700;
          color: #fde68a;
          line-height: 1.25;
        }
        .fab-nudge-close {
          flex-shrink: 0;
          width: 22px; height: 22px;
          display: flex; align-items: center; justify-content: center;
          border: none;
          border-radius: 50%;
          background: rgba(255, 255, 255, 0.08);
          color: #a1a1aa;
          font-size: 0.8rem;
          cursor: pointer;
          transition: background 0.15s ease, color 0.15s ease;
        }
        .fab-nudge-close:hover { background: rgba(255, 255, 255, 0.16); color: #fff; }

        /* ===== TRIGGER BUTTON ===== */
        .fab-button {
          width: 58px; height: 58px;
          border-radius: 50%;
          border: none;
          cursor: pointer;
          display: flex; align-items: center; justify-content: center;
          color: #fff;
          /* Dark glass with a purple rim-light rather than a solid violet disc —
             the flat bright orb read as a sticker pasted over the page. */
          background:
            radial-gradient(circle at 34% 24%, rgba(216, 180, 254, 0.30), rgba(216, 180, 254, 0) 52%),
            linear-gradient(155deg, rgba(46, 26, 82, 0.96) 0%, rgba(24, 16, 44, 0.98) 55%, rgba(14, 10, 26, 0.99) 100%);
          backdrop-filter: blur(10px);
          border: 1px solid rgba(168, 85, 247, 0.55);
          box-shadow:
            0 10px 30px rgba(0, 0, 0, 0.62),
            0 0 26px rgba(168, 85, 247, 0.28),
            inset 0 1px 1px rgba(216, 180, 254, 0.35),
            inset 0 -8px 16px rgba(10, 6, 22, 0.7);
          transition: transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease;
        }
        /* Slow conic shimmer rotating behind the button for a premium feel */
        .fab-button::after {
          content: '';
          position: absolute;
          inset: 0;
          border-radius: 50%;
          background: conic-gradient(from 0deg, transparent 0deg, rgba(216, 180, 254, 0.22) 60deg, transparent 130deg);
          opacity: 0.55;
          mix-blend-mode: screen;
          animation: fab-shimmer 6s linear infinite;
          pointer-events: none;
        }
        @keyframes fab-shimmer {
          to { transform: rotate(360deg); }
        }
        .fab-button::before {
          content: '';
          position: absolute;
          inset: -4px;
          border-radius: 50%;
          border: 1px solid rgba(168, 85, 247, 0.4);
          opacity: 0;
          animation: fab-ring 2.6s ease-in-out infinite;
          pointer-events: none;
        }
        @keyframes fab-ring {
          0%, 100% { opacity: 0; transform: scale(0.92); }
          50%      { opacity: 0.7; transform: scale(1.06); }
        }
        /* Attention mode: gold ring + stronger pulse while something is claimable */
        .fab-button.attention::before {
          border: 2px solid rgba(251, 191, 36, 0.65);
          animation: fab-ring-gold 1.8s ease-in-out infinite;
        }
        @keyframes fab-ring-gold {
          0%, 100% { opacity: 0; transform: scale(0.92); }
          50%      { opacity: 0.95; transform: scale(1.12); }
        }
        .fab-button.attention {
          border-color: rgba(251, 191, 36, 0.6);
          box-shadow:
            0 10px 30px rgba(0, 0, 0, 0.62),
            0 0 30px rgba(251, 191, 36, 0.34),
            inset 0 1px 1px rgba(253, 230, 138, 0.35),
            inset 0 -8px 16px rgba(10, 6, 22, 0.7);
        }
        .fab-button:hover {
          transform: scale(1.06) translateY(-2px);
          border-color: rgba(192, 132, 252, 0.9);
          box-shadow:
            0 14px 36px rgba(0, 0, 0, 0.66),
            0 0 40px rgba(168, 85, 247, 0.42),
            inset 0 1px 1px rgba(216, 180, 254, 0.45);
        }
        .fab-button-icon {
          position: relative;
          z-index: 1;
          transition: transform 0.3s cubic-bezier(0.22, 1, 0.36, 1);
          line-height: 1;
          font-size: 1.65rem;
          font-weight: 400;
          color: #e9d5ff;
          text-shadow: 0 2px 8px rgba(0, 0, 0, 0.6);
        }
        .fab-button.open {
          background:
            radial-gradient(circle at 34% 24%, rgba(216, 180, 254, 0.22), rgba(216, 180, 254, 0) 52%),
            linear-gradient(155deg, rgba(66, 38, 116, 0.97) 0%, rgba(34, 22, 60, 0.98) 60%, rgba(18, 12, 32, 0.99) 100%);
          border-color: rgba(192, 132, 252, 0.85);
        }
        .fab-button.open .fab-button-icon { transform: rotate(135deg); }

        /* Notification dot on the orb */
        .fab-dot {
          position: absolute;
          top: -2px; right: -2px;
          min-width: 20px; height: 20px;
          padding: 0 5px;
          box-sizing: border-box;
          border-radius: 999px;
          background: linear-gradient(135deg, #fbbf24, #f59e0b);
          border: 2px solid #0c0c12;
          color: #1c1205;
          font-size: 0.66rem;
          font-weight: 900;
          display: flex; align-items: center; justify-content: center;
          z-index: 2;
          animation: fab-dot-pop 0.4s cubic-bezier(0.22, 1, 0.36, 1);
        }
        @keyframes fab-dot-pop {
          from { transform: scale(0); }
          to   { transform: scale(1); }
        }

        @media (max-width: 520px) {
          .fab-root { bottom: 16px; right: 16px; }
          .fab-menu { width: min(276px, calc(100vw - 32px)); }
          .fab-nudge-text { font-size: 0.74rem; }
        }
      `}</style>

      {open && (
        <div className="fab-menu" role="menu">
          <div className="fab-menu-header">
            <span className="fab-menu-title">Quick Actions</span>
            <span className="fab-menu-header-line" />
          </div>
          {options.map((opt, i) => {
            const accent = opt.accent || PURPLE;
            return (
              <button
                key={i}
                className={`fab-option ${opt.badge ? 'badged' : ''}`}
                role="menuitem"
                onClick={() => { setOpen(false); opt.onClick(); }}
                style={{
                  '--fab-accent-bg': `linear-gradient(135deg, ${accent}2e, ${accent}14)`,
                  '--fab-accent-border': `${accent}55`
                }}
              >
                <span className="fab-option-icon">{opt.icon}</span>
                <span className="fab-option-body">
                  <span className="fab-option-label">
                    {opt.label}
                    {opt.badge && <span className="fab-option-badge">{opt.badge}</span>}
                  </span>
                  {opt.sublabel && <span className="fab-option-sublabel">{opt.sublabel}</span>}
                </span>
                <span className="fab-option-arrow">→</span>
              </button>
            );
          })}
        </div>
      )}

      <div className="fab-nudge-row">
        {showNudge && (
          <div className="fab-nudge" onClick={() => { dismissNudge(); nudge.onClick(); }} role="button" title={nudge.text}>
            <span className="fab-nudge-icon">{nudge.icon}</span>
            <span className="fab-nudge-text">{nudge.text}</span>
            <button
              className="fab-nudge-close"
              onClick={(e) => { e.stopPropagation(); dismissNudge(); }}
              aria-label="Dismiss"
              title="Dismiss"
            >
              ×
            </button>
          </div>
        )}
        <button
          className={`fab-button ${open ? 'open' : ''} ${attention && !open ? 'attention' : ''}`}
          onClick={() => setOpen(!open)}
          title="Quick actions"
          aria-label="Quick actions"
          aria-expanded={open}
          style={{ position: 'relative' }}
        >
          {attention && !open && <span className="fab-dot">{badgedCount}</span>}
          <span className="fab-button-icon">＋</span>
        </button>
      </div>
    </div>
  );
}
